import { useAppSelector, useAppDispatch } from "@/core/app/hooks"
import { Button, Dialog, DialogActions, DialogContent, DialogTitle } from "@mui/material"
import { setIsDoingPresetup, setStageShowType, setIsCreateSelfplanRefinement, setIsCreateNewRefinement, setShowJsPreview, setShouldCloseTab, setCurLeftPage } from "@/UDTreat/udTreatSlice"
import { RootState } from "@/core/app/store"
import { getCaseById, updateCaseById, caseConversionAction } from "@/core/app/slices/case/caseThunkApi"
import { uploadPhotoJSON } from "@/core/app/slices/records/photograph/photographThunkApi"
import { useLocation, useNavigate, useParams, useSearchParams } from "react-router-dom"
import { safeCloseWindow } from "@/core/app/CloseTabContext"
import { openRefinement } from "@/core/app/slices/clinical/refinement/refinementThunkApi"
import { setCaseRAtype } from "@/UDTreat/udTreatSlice"
import { CASE_TYPE } from "@/core/constants/case.constants"
import { resetPrescriptionSchema, resetSchema } from "@/core/app/slices/uassist/formSlice"
import { cleanUpDataforNew } from "@/core/app/slices/clinical/clinicalSlice"
import { CASE_ACTION } from "@/core/constants/caseActionType"

export const ErrorDialogBox = (props: any) => {
  const { caseDetail } = useAppSelector((state: RootState) => state.caseService)
  const { caseId, patientId } = useParams()
  const { refinementNum } = useAppSelector((state: RootState) => state.udTreatService)
  const dispatch = useAppDispatch()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const errorContent = (type: string) => {
    switch (type) {
      case "failed":
      case "refinefailed":
        return "Pre-setup failed due to a processing error. This may be caused by a temporary issue with cloud computing services. Please choose one of the following options to proceed:"
      case "network error":
      case "refine network error":
        return "A server connection issue may have occurred. You can select Retry or choose one of the options below:"
      case "retry":
      case "refineretry":
        return "Scan data may be incomplete or compromised. If pre-setup keeps failing, a new scan may be required. You can select Retry or choose one of the options below:"
    }
  }

  const btnStyle = {
    padding: "6px 16px 6px 16px",
    borderRadius: "4px",
    boxShadow:
      "0px 1px 5px 0px rgba(0, 0, 0, 0.12), 0px 2px 2px 0px rgba(0, 0, 0, 0.14), 0px 3px 1px -2px rgba(0, 0, 0, 0.20)",
    backgroundColor: "#E0E0E0",
    color: "rgba(0,0,0,0.87)", 
    fontSize: "14px",
    fontWeight: 500,
    letterSpacing: "0.4px",
    whiteSpace: 'nowrap',
    '&:hover': {
      backgroundColor: '#d8d4d4ff', 
    },
  }

  return (
    <Dialog
      sx={{
        "& .MuiDialog-paper": {
          width: "606px",
        },
      }}
      open={props.isShow}
    >
      <DialogTitle>Pre-Setup Failed</DialogTitle>
      <DialogContent
       sx={{
        padding: "20px 24px 20px 24px",
       }}
      >
        <span style={{ fontSize: "16px", letterSpacing: "0.15px", fontWeight: 400, lineHeight: "150%"}}>
          <br />{errorContent(props.errorType)} <br /><br />
          {"\u2022 Submit the case to uAssist"} <br />
          {props.errorType.includes("refine")? "\u2022 Open the case in the uDesign Desktop application and redo the refinement" : "\u2022 Open the case in the uDesign Desktop application to complete pre-setup"} <br />
        </span> 
      </DialogContent>
      <DialogActions>
        <>
          {props.errorType !== "failed" && props.errorType !== "refinefailed" && (
            <Button
              onClick={() => {
                props.setIsShowPresetupError(false)
                const udesign_json = JSON.parse(caseDetail.udesign_json)
                const hasUpper = udesign_json.upperUploaded
                const hasLower = udesign_json.lowerUploaded
                props.requestAiPresetup(hasUpper, hasLower, udesign_json)
              }}
              sx={btnStyle}
              variant={"contained"}
            > Retry </Button>
          )}
          <Button
            onClick={async () => {
              props.setIsShowPresetupError(false)
              const udesign_json = JSON.parse(caseDetail.udesign_json)
              if (udesign_json.isDoingRefinePresetup) {
                await dispatch(caseConversionAction({ patientId, caseId, payload: { action: CASE_ACTION.COMPLETE_PRESETUP}}))
                dispatch(setIsCreateNewRefinement(false))
                dispatch(setIsCreateSelfplanRefinement(false))
                dispatch(setIsDoingPresetup(false))
                dispatch(setStageShowType("normal"))
                dispatch(setShowJsPreview(false))
                const newUdesignJsonObj = {
                  ...udesign_json,
                  isDoingRefinePresetup: false,
                }
                dispatch(
                  updateCaseById({
                    patientId,
                    caseId,
                    payload: {
                      case_extra_attrs: JSON.stringify(newUdesignJsonObj),
                    },
                  }),
                )
                .then(() => {
                  dispatch(
                  uploadPhotoJSON({
                    caseId,
                    patientId,
                    jsonObj: null,
                    refinementIndex: refinementNum,
                    callback: () => {
                      dispatch(getCaseById({ caseId, patientId }))
                      dispatch(setShouldCloseTab(true))
                      safeCloseWindow()
                      return
                    },
                  }),
                )})  
              } else {
                await dispatch(caseConversionAction({ patientId, caseId, payload: { action: CASE_ACTION.FAIL_PRESETUP}}))
                safeCloseWindow()
                return
              }
            }}
            variant={"contained"}
            sx={btnStyle}
          >Close case and continue on udesign</Button>
          <Button
            onClick={async () => {
              props.setIsShowPresetupError(false)
              // todo: open uassist submit page
              await dispatch(caseConversionAction({ patientId, caseId, payload: { action: CASE_ACTION.COMPLETE_PRESETUP}}))
              const udesign_json = JSON.parse(caseDetail.udesign_json)
              if (udesign_json.isDoingRefinePresetup) {
                dispatch(setIsCreateNewRefinement(false))
                dispatch(setIsCreateSelfplanRefinement(false))
                dispatch(setIsDoingPresetup(false))
                dispatch(setStageShowType("normal"))
                dispatch(setShowJsPreview(false))
                dispatch(setCurLeftPage(""))
                const newUdesignJsonObj = {
                  ...udesign_json,
                  isDoingRefinePresetup: false,
                }
                dispatch(
                  updateCaseById({
                    patientId,
                    caseId,
                    payload: {
                      case_extra_attrs: JSON.stringify(newUdesignJsonObj),
                    },
                  }),
                )
                dispatch(openRefinement({ isSelfplan: false }))
              } else {
                await dispatch(caseConversionAction({ patientId, caseId, payload: { action: CASE_ACTION.FAIL_PRESETUP}}))
                await dispatch(getCaseById({ caseId, patientId }))
                dispatch(setCaseRAtype(CASE_TYPE.ALIGNER))
                dispatch(resetPrescriptionSchema())
                dispatch(resetSchema())
                dispatch(cleanUpDataforNew("all"))
                searchParams.set("from", "clinical")
                searchParams.set("action", "submit_to_uassist")
                const params = new URLSearchParams(searchParams).toString()
                navigate(`/records/patient/${patientId}/case/${caseId}/scans?${params}`)
              }
            }}
            sx={btnStyle}
            variant={"contained"}
          >submit to uassist</Button>
        </>
      </DialogActions>
    </Dialog>
  )
}
